import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  PlatformOverviewResult,
  RegionDeleteResult,
  RegionImportRequest,
  RegionImportResult,
  RegionOptionRow,
  RegionQuery,
  RegionRow,
  RegionSaveRequest,
  RegionSaveResult,
  ScreenDeviceApproveRequest,
  ScreenDevicePendingRow,
  ScreenDeviceRow,
  ScreenDeviceTargets,
  TenantAdminPreflightResult,
  TenantAdminRow,
  TenantAdminSaveRequest,
  TenantAdminSaveResult,
  OpsCenterSaveRequest,
  OpsCenterSaveResult,
  TenantCenterRow,
  TenantCreateRequest,
  TenantEnterResult,
  TenantLifecycleResult,
  TenantMetricRow,
  TenantOrgsResult,
  TenantQuery,
  TenantRenewRequest,
  TenantRenewResult,
  TenantSequencePreview,
  TenantRow,
  TenantSaveResult,
  TenantUpdateRequest
} from '../types/tenant';

const TENANT = '/sys/tenant';
const REGION = '/sys/region';
const PLATFORM = '/sys/platform';
const SCREEN_DEVICE = '/sys/screen-device';

/**
 * 平台运营端（web-system）：租户开通 / 编辑 / 停用 / 恢复 / 总览，以及行政区划字典。
 * 权限：`sys:tenant:view|manage|lifecycle`、`sys:region:manage`、`sys:platform:metric:view`；
 * 后端要求门户为 `SYSTEM`（`TenantService.EnsurePlatform`，管理员也不会被短路放行）。
 */
export function createTenantApi(http: HttpClient) {
  return {
    list(query: TenantQuery = {}): Promise<TenantRow[]> {
      return http.request<TenantRow[]>({ path: TENANT, query: toQuery(query) });
    },

    detail(id: Id): Promise<TenantRow> {
      return http.request<TenantRow>({ path: `${TENANT}/${id}` });
    },

    /** 租户下机构与运营中心（只读核对树）。 */
    orgs(id: Id): Promise<TenantOrgsResult> {
      return http.request<TenantOrgsResult>({ path: `${TENANT}/${id}/orgs` });
    },

    /** 租户运营端（OPS）管理员列表（level=admin 的开办账号）。 */
    admins(id: Id): Promise<TenantAdminRow[]> {
      return http.request<TenantAdminRow[]>({ path: `${TENANT}/${id}/admins` });
    },

    /**
     * 运营端管理员账号预检（不占号）：新增表单只读回显系统将生成的登录账号。
     * 账号由系统生成，不再接受人工输入。
     */
    adminPreflight(id: Id): Promise<TenantAdminPreflightResult> {
      return http.request<TenantAdminPreflightResult>({ path: `${TENANT}/${id}/admins/preflight` });
    },

    /** 新增 / 维护运营端管理员（带 id 为维护，password 留空不改口令；`account` 由服务端生成/保持）。 */
    saveAdmin(id: Id, body: TenantAdminSaveRequest): Promise<TenantAdminSaveResult> {
      return http.request<TenantAdminSaveResult>({ path: `${TENANT}/${id}/admins`, method: 'POST', body });
    },

    /** 开通租户（同步创建监管单位、运营中心与首个运营端管理员）。 */
    create(body: TenantCreateRequest): Promise<TenantSaveResult> {
      return http.request<TenantSaveResult>({ path: TENANT, method: 'POST', body });
    },

    update(id: Id, body: TenantUpdateRequest): Promise<TenantSaveResult> {
      return http.request<TenantSaveResult>({ path: `${TENANT}/${id}`, method: 'PUT', body });
    },

    suspend(id: Id): Promise<TenantLifecycleResult> {
      return http.request<TenantLifecycleResult>({ path: `${TENANT}/${id}/suspend`, method: 'POST' });
    },

    resume(id: Id): Promise<TenantLifecycleResult> {
      return http.request<TenantLifecycleResult>({ path: `${TENANT}/${id}/resume`, method: 'POST' });
    },

    /**
     * 进入租户视角（docs/07 §7.4）：签发 15 分钟**只读**令牌（`tid=目标租户`、`ent=1`），
     * 同时落 `PLATFORM_ENTER_TENANT` 审计；到期自动失效，不可用于写操作。
     */
    enter(id: Id): Promise<TenantEnterResult> {
      return http.request<TenantEnterResult>({ path: `${TENANT}/${id}/enter`, method: 'POST' });
    },

    /** 续约：更新到期日并恢复为 RUNNING（`sys:tenant:lifecycle`）。 */
    renew(id: Id, body: TenantRenewRequest): Promise<TenantRenewResult> {
      return http.request<TenantRenewResult>({ path: `${TENANT}/${id}/renew`, method: 'POST', body });
    },

    /** 置为到期（`sys:tenant:lifecycle`）：立即踢出全部会话。 */
    expire(id: Id): Promise<TenantLifecycleResult> {
      return http.request<TenantLifecycleResult>({ path: `${TENANT}/${id}/expire`, method: 'POST' });
    },

    /** 编号规则预览：按当前模板推算下一条样号，不消耗序号（`sys:tenant:view`）。 */
    sequences(id: Id, bizType?: string): Promise<TenantSequencePreview[]> {
      return http.request<TenantSequencePreview[]>({
        path: `${TENANT}/${id}/sequences`,
        query: toQuery({ bizType })
      });
    },

    /** 运营中心清单（租户详情运营中心 Tab）。 */
    centers(id: Id): Promise<TenantCenterRow[]> {
      return http.request<TenantCenterRow[]>({ path: `${TENANT}/${id}/centers` });
    },

    /** 新增 / 维护运营中心（`sys:opscenter:manage`）。 */
    saveCenter(id: Id, body: OpsCenterSaveRequest): Promise<OpsCenterSaveResult> {
      return http.request<OpsCenterSaveResult>({ path: `${TENANT}/${id}/centers`, method: 'POST', body });
    },

    /** 删除运营中心（仍有账号归属时后端拒绝）。 */
    removeCenter(id: Id, centerId: Id): Promise<{ id: string; deleted: boolean }> {
      return http.request<{ id: string; deleted: boolean }>({
        path: `${TENANT}/${id}/centers/${centerId}`,
        method: 'DELETE'
      });
    },

    /** 平台总览：租户状态分布 + 机构/监管/运营中心/账号/区划计数。 */
    overview(): Promise<PlatformOverviewResult> {
      return http.request<PlatformOverviewResult>({ path: `${PLATFORM}/overview` });
    },

    /** 各租户用量与配额水位（`sys:platform:metric:view`）。 */
    tenantMetric(tenantId?: Id): Promise<TenantMetricRow[]> {
      return http.request<TenantMetricRow[]>({ path: `${PLATFORM}/tenant-metric`, query: toQuery({ tenantId }) });
    },

    /** 区划字典：可按 parentCode / level 过滤（后端 `GET /api/sys/region`）。 */
    regions(query: RegionQuery = {}): Promise<RegionRow[]> {
      return http.request<RegionRow[]>({ path: REGION, query: toQuery(query) });
    },

    /**
     * 区划选项（省/市/区三级，供机构/医共体/运营仓/仓库表单级联）。
     * 与 `regions` 不同：不做 SYSTEM 门户门禁，OPS 门户登录即可读。
     */
    regionOptions(): Promise<RegionOptionRow[]> {
      return http.request<RegionOptionRow[]>({ path: `${REGION}/options` });
    },

    /**
     * 全国行政区划导入（`public/china-regions.json` → `sys_region`）。
     * 需要 `sys:region:manage` + SYSTEM 门户。
     */
    importRegions(body: RegionImportRequest): Promise<RegionImportResult> {
      return http.request<RegionImportResult>({ path: `${REGION}/import`, method: 'POST', body });
    },

    /** 新增（缺 `id`）或更新（带 `id`）区划。 */
    saveRegion(body: RegionSaveRequest): Promise<RegionSaveResult> {
      return http.request<RegionSaveResult>({ path: REGION, method: 'POST', body });
    },

    /** 更新区划（等价于 `saveRegion` 带 id）。 */
    updateRegion(id: string, body: RegionSaveRequest): Promise<RegionSaveResult> {
      return http.request<RegionSaveResult>({ path: `${REGION}/${id}`, method: 'PUT', body });
    },

    /** 删除区划：有下级区划或已被租户引用时后端返回 42200。 */
    removeRegion(id: string): Promise<RegionDeleteResult> {
      return http.request<RegionDeleteResult>({ path: `${REGION}/${id}`, method: 'DELETE' });
    },

    // ---------------- 大屏设备（扫码授权，租户级）----------------

    /** 已授权大屏设备（扫码授权，凭据可逐屏吊销）；tenantId 仅平台账号有效。 */
    screenDevices(tenantId?: Id): Promise<ScreenDeviceRow[]> {
      return http.request<ScreenDeviceRow[]>({
        path: SCREEN_DEVICE,
        query: tenantId ? { tenantId: String(tenantId) } : undefined
      });
    },

    /** 扫码确认页的目标范围（按扫码人门户返回：平台可选租户、运营选机构、监管/医院固定本人机构）。 */
    screenDeviceTargets(tenantId?: Id): Promise<ScreenDeviceTargets> {
      return http.request<ScreenDeviceTargets>({
        path: `${SCREEN_DEVICE}/targets`,
        query: tenantId ? { tenantId: String(tenantId) } : undefined
      });
    },

    /** 待确认的大屏申请（运营/系统端监控用，只读；确认需扫屏上的编码）。 */
    screenDevicePending(): Promise<ScreenDevicePendingRow[]> {
      return http.request<ScreenDevicePendingRow[]>({ path: `${SCREEN_DEVICE}/pending` });
    },

    /** 确认授权（电脑端「申请码确认」用）：把这块屏绑定到指定机构，监管/医院的机构由令牌决定。 */
    approveScreenDevice(code: string, body: ScreenDeviceApproveRequest = {}): Promise<{
      status: string;
      deviceName: string;
      bizType: string;
      bizName: string;
      tenantId: string;
      tenantName: string | null;
      orgId: string;
      orgName: string | null;
    }> {
      return http.request({
        path: `${SCREEN_DEVICE}/${encodeURIComponent(code)}/approve`,
        method: 'POST',
        body
      });
    },

    /** 吊销大屏设备凭据：下一次换票即失败。 */
    disableScreenDevice(id: Id): Promise<{ id: string; status: string }> {
      return http.request<{ id: string; status: string }>({
        path: `${SCREEN_DEVICE}/${id}/disable`,
        method: 'POST'
      });
    }
  };
}

export type TenantApi = ReturnType<typeof createTenantApi>;
